import { useRef, useState, useEffect } from 'react';
import { styled, useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { TopSection, BottomSectionContainer, PageContainer, MainScrollArea } from './component';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Modal, ModalBody, ModalFooter } from '@tigergraph/app-ui-lib/modal';
import { Body2 } from '@tigergraph/app-ui-lib/typography';
import { Stepper } from '@/pages/onboarding/Stepper';
import { useQueryGraphStatistics } from '@/pages/dashboard/hook';
import { useAtom, useAtomValue } from 'jotai';
import { onboardingAtom } from './state';
import { LoadingIndicator } from '@/components/loading-indicator';
import { ErrorDisplay } from '@/components/error';
import { useNavigate } from 'react-router-dom';
import ResultSuccessIcon from './icon/result_success.svg';
import ActionExploreIcon from './icon/action_explore.svg?react';
import ActionProfileIcon from './icon/action_profile.svg?react';
import ActionEditorIcon from './icon/action_editor.svg?react';
import { useSchema, useSchemaStyle } from '@/utils/graphEditor/useMetaHook';
import { GLOBAL_GRAPH_NAME, Graph } from '@tigergraph/tools-models';
import { GraphResultRef } from '@/pages/editor/result/GraphResult';
import { useSchemaDesignerContext } from '@/contexts/schemaDesignerContext';
import SchemaDesigner from '@/components/schemaDesigner';
import OnboardingWrapper from '@/pages/onboarding/Wrapper';

const StatBoxWrapper = styled('div', {
  display: 'flex',
  gap: '32px',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: '24px',
});

const StatBox = styled('div', ({ $theme }) => ({
  background: $theme.colors['background.primary'],
  borderRadius: '12px',
  padding: '24px 32px',
  minWidth: '160px',
  textAlign: 'center',
  border: `1px solid ${$theme.colors['border.tertiary']}`,
}));

const StatLabel = styled('div', ({ $theme }) => ({
  fontSize: '14px',
  color: $theme.colors['text.secondary'],
  marginBottom: '8px',
}));

const StatValue = styled('div', ({ $theme }) => ({
  fontSize: '24px',
  fontWeight: 600,
  color: $theme.colors.contentPrimary,
}));

const SectionTitle = styled('div', ({ $theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: $theme.colors.contentPrimary,
  marginBottom: '8px',
}));

const InsightsWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const InsightCard = styled('div', ({ $theme }) => ({
  background: $theme.colors['background.primary'],
  border: `1px solid ${$theme.colors['border.tertiary']}`,
  borderRadius: '8px',
  padding: '16px',
}));

const InsightTitle = styled('div', ({ $theme }) => ({
  fontWeight: 500,
  fontSize: '16px',
  marginBottom: '4px',
  color: $theme.colors.contentPrimary,
}));

export default function ViewResultWrapper() {
  return (
    <OnboardingWrapper fallbackToImportCSV={true}>
      <ViewResult />
    </OnboardingWrapper>
  );
}

function ViewResult() {
  const [css, theme] = useStyletron();
  const [isModalOpen, setModalOpen] = useState(false);

  const [{ graph_name, workspace }] = useAtom(onboardingAtom);
  const { data, isLoading, isError, error } = useQueryGraphStatistics(workspace?.nginx_host || '', graph_name || '', {
    enabled: !!graph_name,
  });
  const { data: schema } = useSchema(graph_name || GLOBAL_GRAPH_NAME, workspace!);
  const { data: schemaStyle } = useSchemaStyle(graph_name || GLOBAL_GRAPH_NAME, workspace!);

  const graphResultRef = useRef<GraphResultRef>(null);
  const { designerService } = useSchemaDesignerContext();

  useEffect(() => {
    if (schema) {
      designerService.setSchemaAndStyle(schema, schemaStyle);
      graphResultRef.current?.renderChart();
      graphResultRef.current?.graphRef.current?.runLayout(
        {
          name: 'fcose',
          fit: true,
        },
        () => {
          graphResultRef.current?.centerGraph();
        }
      );
    } else {
      designerService.semanticCheckAndUpdateGraph(new Graph());
      graphResultRef.current?.renderChart();
    }
  }, [designerService, schema, schemaStyle]);

  // Update the stats array with safe checks
  const stats = data
    ? [
        { label: 'Vertex Types', value: data.vertexCounts?.results?.length || 0 },
        { label: 'Edge Types', value: data.edgeCounts?.results?.length || 0 },
        { label: 'Total Vertices', value: data.vertexCounts?.results?.reduce((acc, cur) => acc + cur.count, 0) || 0 },
        { label: 'Total Edges', value: data.edgeCounts?.results?.reduce((acc, cur) => acc + cur.count, 0) || 0 },
      ]
    : [];

  const { algorithmResult } = useAtomValue(onboardingAtom);

  return (
    <PageContainer>
      <TopSection showClose />
      <MainScrollArea>
        <Stepper current={3} />
        <div
          style={{
            width: '100%',
            maxWidth: 1288,
            margin: '0 auto',
            flexBasis: 0,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 600, textAlign: 'center', color: 'inherit', marginBottom: 16 }}>
            Your graph is ready to explore!
          </div>
          <StatBoxWrapper>
            {isLoading ? (
              <LoadingIndicator />
            ) : isError ? (
              <ErrorDisplay error={error} />
            ) : (
              stats.map((stat) => (
                <StatBox key={stat.label}>
                  <StatLabel>{stat.label}</StatLabel>
                  <StatValue>{stat.value}</StatValue>
                </StatBox>
              ))
            )}
          </StatBoxWrapper>
          <div style={{ display: 'flex', gap: 32, flexBasis: 0, flexGrow: 1, marginBottom: 24 }}>
            <div style={{ flex: 2, minWidth: 0 }}>
              <div
                className={css({
                  height: '100%',
                })}
              >
                <SchemaDesigner
                  ref={graphResultRef}
                  currentWorkspace={workspace!}
                  currentGraph={graph_name || GLOBAL_GRAPH_NAME}
                  height="100%"
                  layout={'Force'}
                  backgroundColor={theme.colors['background.secondary']}
                  enableEditor={false}
                  enableCreate={false}
                  enableDelete={false}
                  enableSave={false}
                  enableShortcuts={false}
                  enableGraphSelect={false}
                  enableFullScreen={false}
                  enableHeader={false}
                  enableUndoRedo={false}
                  hideSearch={true}
                  width="100%"
                  schemaJson={{ results: schema }}
                  showCreateEdgeTooltip={false}
                />
              </div>
            </div>
            {algorithmResult.length > 0 && (
              <div style={{ flex: 1, minWidth: 320, height: '100%', overflowY: 'auto' }}>
                <SectionTitle>Key Insights</SectionTitle>
                <InsightsWrapper>
                  {algorithmResult.map((insight, idx) => (
                    <InsightCard key={insight.title}>
                      <InsightTitle>{insight.title}</InsightTitle>
                      <div>{insight.desc}</div>
                    </InsightCard>
                  ))}
                </InsightsWrapper>
              </div>
            )}
          </div>
        </div>
      </MainScrollArea>
      <BottomSectionContainer>
        <Button kind="primary" size="large" onClick={() => setModalOpen(true)}>
          Get Started
        </Button>
      </BottomSectionContainer>
      <ExploreModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </PageContainer>
  );
}

function ExploreModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [{ workspace, graph_name }] = useAtom(onboardingAtom);
  const [css, theme] = useStyletron();
  const navigate = useNavigate();

  const options = [
    {
      icon: <ActionExploreIcon />,
      iconBgColor: '#DBEAFE',
      title: 'Explore Graph',
      desc: 'Visually explore your graph with AI-suggested queries and intuitive pattern discovery tools.',
      label: 'Beginner Friendly',
      button: 'Explore My Graph',
      onClick: () => {
        navigate(`/explore?workspace_id=${workspace?.workspace_id}&graph_name=${encodeURIComponent(graph_name || '')}`);
      },
    },
    {
      icon: <ActionProfileIcon />,
      iconBgColor: '#F3E8FF',
      title: 'Data Profile',
      desc: 'Get an instant overview of your schema, vertex types, and relationship patterns.',
      label: 'Insight Driven',
      button: 'View Data Profile',
      onClick: () => {
        navigate(
          `/groups/${workspace?.workgroup_id}?workspace_id=${workspace?.workspace_id}&graph_name=${encodeURIComponent(
            graph_name || ''
          )}&tab=dataprofiles`
        );
      },
    },
    {
      icon: <ActionEditorIcon />,
      iconBgColor: '#DCFCE7',
      title: 'GSQL Editor',
      desc: 'Write and execute custom queries for complete control over your graph exploration.',
      label: 'For Advanced Users',
      button: 'Open GSQL Editor',
      onClick: () => {
        navigate(`/editor?workspace_id=${workspace?.workspace_id}&graph_name=${encodeURIComponent(graph_name || '')}`);
      },
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="auto" closeable={false}>
      <ModalBody
        className={css({
          padding: '24px 80px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        })}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <img src={ResultSuccessIcon} alt="Result Success" />
          <div style={{ fontWeight: 600, fontSize: 24, lineHeight: '40px', fontFamily: 'Urbanist' }}>
            Choose how you’d like to explore your graph
          </div>
          <Body2 $style={{ color: theme.colors['text.secondary'], textAlign: 'center' }}>
            Based on your comfort level, choose a method to dive deeper into your generated graph. You can also skip and
            explore on your own later.
          </Body2>
        </div>
        <CardGrid>
          {options.map((opt) => (
            <Card key={opt.title}>
              <CardIcon $style={{ backgroundColor: opt.iconBgColor }}>{opt.icon}</CardIcon>
              <CardLabel>{opt.label}</CardLabel>
              <CardTitle>{opt.title}</CardTitle>
              <CardDesc>{opt.desc}</CardDesc>
              <CardButton kind="primary" shape="square" size="large" onClick={opt.onClick}>
                {opt.button}
              </CardButton>
            </Card>
          ))}
        </CardGrid>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" size="large" onClick={() => navigate('/')}>
          Explore on my own
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const CardGrid = styled('div', {
  display: 'flex',
  gap: '32px',
  justifyContent: 'center',
});
const Card = styled('div', ({ $theme }) => ({
  borderRadius: '2px',
  padding: '18px',
  width: '260px',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${$theme.colors['border.tertiary']}`,
}));

const CardIcon = styled('div', ({ $theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '12px',
}));

const CardLabel = styled('div', ({ $theme }) => ({
  fontSize: '12px',
  color: '#FF7A00',
  backgroundColor: 'rgba(255, 122, 0, 0.10)',
  fontWeight: 500,
  marginBottom: '8px',
  borderRadius: '100px',
  padding: '4px 8px',
  alignSelf: 'flex-start',
}));

const CardTitle = styled('div', ({ $theme }) => ({
  fontSize: '20px',
  lineHeight: '28px',
  fontWeight: 600,
  marginBottom: '8px',
  color: $theme.colors.contentPrimary,
}));
const CardDesc = styled('div', ({ $theme }) => ({
  color: $theme.colors['text.secondary'],
  marginBottom: '16px',
}));
const CardButton = styled(Button, {
  width: '100%',
  marginTop: 'auto',
});
